import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Chip,
} from "@mui/material";
import API from "../../utils/api";
import { tSuccess, tError } from "../../utils/toast";
import { getUserInitials } from "../../utils/userInitials";

/**
 * Transfer Conversation Modal
 * Wired to backend: POST /api/conversations/{id}/transfer/
 */

export default function TransferConversationModal({
  open,
  onClose,
  conversationId,
  users = [],
  onTransferred,
}) {
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);

  const getDisplayName = (u) =>
    [u.first_name, u.last_name].filter(Boolean).join(" ").trim() || u.username || "";

  const filtered = users.filter((u) =>
    getDisplayName(u).toLowerCase().includes(search.trim().toLowerCase())
  );

  const handleConfirm = async () => {
    if (!selectedUser || !conversationId) return;

    try {
      setLoading(true);

      await API.post(
        `/conversations/${conversationId}/transfer/`,
        {
          assigned_to: selectedUser.id,
        }
      );

      tSuccess(
        `Conversation transferred to ${getDisplayName(selectedUser) || selectedUser.username}`
      );

      onClose();
      setSelectedUser(null);
      setSearch("");

      if (onTransferred) {
        onTransferred();
      }
    } catch (err) {
      console.error(err);
      tError("Failed to transfer conversation");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      slotProps={{ root: { sx: { zIndex: 2300 } } }}
      ModalProps={{ sx: { zIndex: 2300 } }}
      PaperProps={{
        sx: {
          zIndex: 2000,
          borderRadius: 3,
          backgroundImage: (theme) =>
            theme.palette.mode === "dark"
              ? "linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0))"
              : "linear-gradient(180deg, rgba(255,255,255,0.7), rgba(255,255,255,0))",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          bgcolor: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(2, 6, 23, 0.87)"
              : "rgba(255, 255, 255, 0.87)",
          border: (theme) =>
            theme.palette.mode === "dark"
              ? "1px solid rgba(255,255,255,0.12)"
              : "1px solid rgba(0,0,0,0.08)",
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "0 25px 70px rgba(0,0,0,0.8)"
              : "0 25px 70px rgba(0,0,0,0.25)",
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
        Transfer Conversation
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Select a team member to take over this conversation.
        </Typography>

        <TextField
          fullWidth
          size="small"
          placeholder="Search team members…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
        />

        <List dense>
          {filtered.map((user) => (
            <ListItemButton
              key={user.id}
              selected={selectedUser?.id === user.id}
              onClick={() => setSelectedUser(user)}
              sx={{
                borderRadius: 2,
                mb: 0.5,
              }}
            >
              <ListItemAvatar>
                <Avatar>
                  {getUserInitials(
                    {
                      first_name: user.first_name,
                      last_name: user.last_name,
                      full_name: getDisplayName(user),
                      username: user.username,
                    },
                    "U"
                  )}
                </Avatar>
              </ListItemAvatar>

              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography fontWeight={700}>
                      {getDisplayName(user) || "Team Member"}
                    </Typography>
                    {user.is_available !== undefined && (
                      <Chip
                        label={user.is_available ? "Available" : "Busy"}
                        size="small"
                        color={user.is_available ? "success" : "default"}
                      />
                    )}
                  </Box>
                }
                secondary={user.role || "Team Member"}
              />
            </ListItemButton>
          ))}

          {filtered.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 2, textAlign: "center" }}
            >
              No matching users
            </Typography>
          )}
        </List>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>

        <Button
          variant="contained"
          disabled={!selectedUser || loading}
          onClick={handleConfirm}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          {loading ? "Transferring…" : "Transfer"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
